import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Star, Info, Lightbulb, Shield, Zap, BookOpen, HelpCircle, PlayCircle } from 'lucide-react';
import { Tool, getAllTools } from '../data/tools';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { useLanguageRouter } from '../utils/i18nRouter';
import { Button } from './ui/Button';

interface ToolPageTemplateProps {
  tool: Tool;
  children: React.ReactNode;
  className?: string;
}

export const ToolPageTemplate: React.FC<ToolPageTemplateProps> = ({
  tool,
  children,
  className = ''
}) => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouter();
  const IconComponent = tool.icon;

  // 获取相关工具
  const getRelatedTools = (currentTool: Tool): Tool[] => {
    const allTools = getAllTools();

    // 定义工具关联规则
    const relatedToolsMap: Record<string, string[]> = {
      'json-formatter': ['xml-formatter', 'yaml-converter', 'csv-to-json', 'base64-encoder'],
      'xml-formatter': ['json-formatter', 'yaml-converter', 'html-encoder'],
      'yaml-converter': ['json-formatter', 'xml-formatter', 'csv-to-json'],
      'base64-encoder': ['url-encoder', 'hash-generator', 'json-formatter'],
      'url-encoder': ['base64-encoder', 'html-encoder', 'hash-generator'],
      'hash-generator': ['base64-encoder', 'url-encoder', 'password-generator'],
      'password-generator': ['hash-generator', 'uuid-generator', 'password-strength-checker'],
      'uuid-generator': ['password-generator', 'hash-generator', 'random-generator'],
      'color-converter': ['image-color-picker', 'css-minifier', 'image-converter'],
      'qr-generator': ['base64-encoder', 'url-encoder', 'image-converter'],
      'csv-to-json': ['json-formatter', 'xml-formatter', 'yaml-converter'],
      'json-to-csv': ['csv-to-json', 'json-formatter', 'xml-formatter'],
      'text-diff': ['markdown-editor', 'text-counter', 'case-converter'],
      'markdown-editor': ['text-diff', 'text-counter', 'html-encoder'],
      'image-compressor': ['image-converter', 'image-crop', 'image-watermark'],
      'image-converter': ['image-compressor', 'image-crop', 'color-converter'],
      'calculator': ['percentage-calculator', 'unit-converter', 'date-calculator'],
      'percentage-calculator': ['calculator', 'unit-converter', 'random-generator'],
      'whois-lookup': ['dns-lookup', 'ip-lookup', 'ssl-certificate-analyzer'],
      'dns-lookup': ['whois-lookup', 'ip-lookup', 'ping-test'],
      'ip-lookup': ['whois-lookup', 'dns-lookup', 'ping-test'],
      'timestamp-converter': ['date-calculator', 'timezone-converter', 'age-calculator'],
      'date-calculator': ['timestamp-converter', 'age-calculator', 'business-days-calculator'],
      'regex-tester': ['text-diff', 'case-converter', 'text-counter'],
      'jwt-decoder': ['base64-encoder', 'hash-generator', 'password-strength-checker'],
      'ssl-certificate-analyzer': ['whois-lookup', 'dns-lookup', 'encryption-tool']
    };

    const relatedIds = relatedToolsMap[currentTool.id] || [];

    // 如果没有预定义的关联，则按分类获取同类工具
    if (relatedIds.length === 0) {
      return allTools
        .filter(t => t.category === currentTool.category && t.id !== currentTool.id)
        .slice(0, 4);
    }

    return allTools
      .filter(t => relatedIds.includes(t.id))
      .slice(0, 4);
  };

  const relatedTools = getRelatedTools(tool);

  // 生成更丰富的结构化数据
  const generateFeatureList = () => {
    const features = [];
    if (tool.keyFeatures && Array.isArray(tool.keyFeatures)) {
      features.push(...tool.keyFeatures.map(feature => t(feature)));
    }
    if (tool.benefits && Array.isArray(tool.benefits)) {
      features.push(...tool.benefits.map(benefit => t(benefit)));
    }
    return features;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "WebApplication"],
    "name": t(tool.name),
    "description": t(tool.longDescription || tool.description),
    "url": `${window.location.origin}${tool.path}`,
    "applicationCategory": "WebApplication",
    "applicationSubCategory": t(`categories.${tool.category}`),
    "operatingSystem": "Any",
    "browserRequirements": "Any modern web browser",
    "softwareVersion": "2.1.0",
    "releaseNotes": "Enhanced SEO optimization and user experience improvements",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2025-12-31"
    },
    "keywords": [
      t(tool.keywords || ''),
      ...(tool.seoKeywords || []),
      t(`categories.${tool.category}`),
      "online tool",
      "free tool",
      "web tool"
    ].filter(Boolean).join(', '),
    "provider": {
      "@type": "Organization",
      "name": t('header.title'),
      "url": window.location.origin,
      "logo": `${window.location.origin}/logo.png`,
      "sameAs": [
        "https://github.com/fushui1"
      ]
    },
    "featureList": generateFeatureList(),
    "usageInfo": tool.useCases && Array.isArray(tool.useCases)
      ? tool.useCases.map(useCase => t(useCase)).join(', ')
      : (tool.useCases ? t(tool.useCases) : ''),
    "category": t(`categories.${tool.category}`),
    "inLanguage": ["en", "zh", "ja", "ko", "es", "fr", "de", "it", "pt", "ru", "ar", "hi", "th", "vi", "tr", "nl", "pl", "sv"],
    "isAccessibleForFree": true,
    "dateCreated": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "datePublished": "2024-01-01",
    "author": {
      "@type": "Organization",
      "name": t('header.title')
    },
    "publisher": {
      "@type": "Organization",
      "name": t('header.title'),
      "url": window.location.origin
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": tool.faq && Array.isArray(tool.faq) ? tool.faq.map((item, index) => ({
        "@type": "Question",
        "name": t(item.question),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t(item.answer)
        }
      })) : []
    },
    "potentialAction": {
      "@type": "UseAction",
      "target": `${window.location.origin}${tool.path}`,
      "object": {
        "@type": "SoftwareApplication",
        "name": t(tool.name)
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "1250",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  // 面包屑导航结构化数据
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": t(`categories.${tool.category}`),
        "item": `${window.location.origin}/#${tool.category}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": t(tool.name),
        "item": `${window.location.origin}${tool.path}`
      }
    ]
  };

  return (
    <div className={`h-full overflow-auto ${className}`} role="main" aria-live="polite">
      <Helmet>
        <title>{t(tool.name)} - Free Online Tool | {t('header.title')}</title>
        <meta name="description" content={`${t(tool.description)} ${t('seo.tool.descriptionSuffix')}`} />
        <meta name="keywords" content={`${t(tool.keywords)}, ${tool.seoKeywords ? tool.seoKeywords.join(', ') + ', ' : ''}${t(`categories.${tool.category}`)}, online tool, free tool, web tool, browser tool`} />
        <meta name="author" content={t('header.title')} />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content={`${t(tool.name)} - Free Online Tool`} />
        <meta property="og:description" content={t(tool.longDescription || tool.description)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}${tool.path}`} />
        <meta property="og:image" content={`${window.location.origin}/og-tool-${tool.id}.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={t('header.title')} />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@toolkitclub" />
        <meta name="twitter:creator" content="@toolkitclub" />
        <meta name="twitter:title" content={`${t(tool.name)} - Free Online Tool`} />
        <meta name="twitter:description" content={t(tool.longDescription || tool.description)} />
        <meta name="twitter:image" content={`${window.location.origin}/og-tool-${tool.id}.png`} />

        {/* Additional SEO */}
        <meta name="theme-color" content="#3B82F6" />
        <meta name="msapplication-TileColor" content="#3B82F6" />
        <link rel="canonical" href={`${window.location.origin}${tool.path}`} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>

        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      </Helmet>

      <div className="p-6 space-y-8">
        {/* Tool Header */}
        <header className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-xl flex items-center justify-center" aria-hidden="true">
              <IconComponent className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white" id="tool-title">
                {t(tool.name)}
              </h1>
              {tool.featured && (
                <div className="flex items-center mt-1">
                  <Star className="w-4 h-4 text-yellow-500 mr-1" />
                  <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                    {t('home.featured')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <p className="text-xl text-gray-600 dark:text-gray-400 mb-4" id="tool-description">
            {t(tool.description)}
          </p>
          
          {/* Long Description */}
          {tool.longDescription && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {t(tool.longDescription)}
              </p>
            </div>
          )}
        </header>

        {/* Tool Component */}
        <section aria-labelledby="tool-title" aria-describedby="tool-description" className="mb-12">
          {children}
        </section>

        {/* Tool Information */}
        <section className="space-y-8">
          {/* Overview: Use Cases & Benefits Combined */}
          {(tool.useCases || tool.benefits) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Lightbulb className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span>{t('tool.sections.overview')}</span>
                </CardTitle>
                <CardDescription>
                  {t('tool.sections.overviewDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Use Cases */}
                  {tool.useCases && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        {t('tool.sections.useCases')}
                      </h4>
                      {Array.isArray(tool.useCases) ? (
                        <ul className="space-y-2">
                          {tool.useCases.slice(0, 4).map((useCase, index) => (
                            <li key={index} className="flex items-start space-x-2 text-sm">
                              <span className="text-blue-500 mt-1">•</span>
                              <span className="text-gray-700 dark:text-gray-300">{t(useCase)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                          {t(tool.useCases)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Benefits */}
                  {tool.benefits && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        {t('tool.sections.benefits')}
                      </h4>
                      {Array.isArray(tool.benefits) ? (
                        <ul className="space-y-2">
                          {tool.benefits.slice(0, 4).map((benefit, index) => (
                            <li key={index} className="flex items-start space-x-2 text-sm">
                              <span className="text-green-500 mt-1">✓</span>
                              <span className="text-gray-700 dark:text-gray-300">{t(benefit)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                          {t(tool.benefits)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Features & Tips Combined */}
          {((tool.keyFeatures && Array.isArray(tool.keyFeatures) && tool.keyFeatures.length > 0) ||
            (tool.proTips && Array.isArray(tool.proTips) && tool.proTips.length > 0)) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span>{t('tool.sections.featuresAndTips')}</span>
                </CardTitle>
                <CardDescription>
                  {t('tool.sections.featuresAndTipsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Key Features */}
                  {tool.keyFeatures && Array.isArray(tool.keyFeatures) && tool.keyFeatures.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-primary-500 rounded-full mr-2"></span>
                        {t('tool.sections.features')}
                      </h4>
                      <ul className="space-y-2">
                        {tool.keyFeatures.slice(0, 4).map((feature, index) => (
                          <li key={index} className="flex items-start space-x-2 text-sm">
                            <span className="text-primary-500 mt-1">•</span>
                            <span className="text-gray-700 dark:text-gray-300">{t(feature)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Pro Tips */}
                  {tool.proTips && Array.isArray(tool.proTips) && tool.proTips.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                        {t('tool.sections.tips')}
                      </h4>
                      <ul className="space-y-2">
                        {tool.proTips.slice(0, 4).map((tip, index) => (
                          <li key={index} className="flex items-start space-x-2 text-sm">
                            <span className="text-yellow-500 mt-1">💡</span>
                            <span className="text-gray-700 dark:text-gray-300">{t(tip)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Usage Guide */}
          {tool.usageGuide && Array.isArray(tool.usageGuide) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span>{t('tool.sections.usageGuide')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  {tool.usageGuide.map((step, index) => (
                    <li key={index} className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                          {t(step)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}

          {/* Help & Examples Combined */}
          {((tool.faq && Array.isArray(tool.faq)) || tool.tutorial) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <HelpCircle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span>{t('tool.sections.helpAndExamples')}</span>
                </CardTitle>
                <CardDescription>
                  {t('tool.sections.helpAndExamplesDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* FAQ - Show only top 3 questions */}
                  {tool.faq && Array.isArray(tool.faq) && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        {t('tool.sections.commonQuestions')}
                      </h4>
                      <div className="space-y-4">
                        {tool.faq.slice(0, 3).map((item, index) => (
                          <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                              {t(item.question)}
                            </h5>
                            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                              {t(item.answer)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Examples - Show only 2 examples */}
                  {tool.tutorial && tool.tutorial.examples && Array.isArray(tool.tutorial.examples) && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        {t('tool.sections.examples')}
                      </h4>
                      <div className="grid gap-4 md:grid-cols-2">
                        {tool.tutorial.examples.slice(0, 2).map((example, index) => (
                          <div key={index} className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
                            <h5 className="font-medium text-gray-900 dark:text-white mb-3 text-sm">
                              {t(example.title)}
                            </h5>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('tool.sections.input')}
                                </label>
                                <div className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs font-mono">
                                  {t(example.input)}
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('tool.sections.output')}
                                </label>
                                <div className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs font-mono">
                                  {t(example.output)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Keywords for SEO */}
          {(tool.keywords || tool.seoKeywords) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Info className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span>{t('tool.sections.keywords')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {tool.keywords && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                        {t('tool.sections.primaryKeywords')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {t(tool.keywords).split(', ').map((keyword, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium"
                          >
                            {keyword.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {tool.seoKeywords && tool.seoKeywords.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                        {t('tool.sections.searchTerms')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {tool.seoKeywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Related Tools */}
        {relatedTools.length > 0 && (
          <section>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span>{t('tool.sections.relatedTools')}</span>
                </CardTitle>
                <CardDescription>
                  {t('tool.sections.relatedToolsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {relatedTools.map((relatedTool) => {
                    const RelatedIcon = relatedTool.icon;
                    return (
                      <div
                        key={relatedTool.id}
                        className="group p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <RelatedIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 dark:text-white text-sm mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                              {t(relatedTool.name)}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                              {t(relatedTool.description)}
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 p-0 h-auto text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                              onClick={() => window.location.href = getLocalizedPath(relatedTool.path)}
                            >
                              {t('tool.sections.tryTool')} →
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </section>
        )}
      </div>
    </div>
  );
};
